import { lazy, Suspense, type ComponentType } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { QueryClient } from '@tanstack/react-query'
import { useBuildVersionCheck } from '@/hooks/use-build-version'
import { useOutboxSync } from '@/hooks/use-outbox-sync'
import { usePhotoOutboxSync } from '@/hooks/use-photo-outbox'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister'
import { useAuthStore } from '@/stores'
import { AuthProvider } from '@/providers'
import { refreshSessionSafe, DEV_BYPASS_AUTH } from '@/lib/supabase/client'
import { ThemeProvider } from '@/providers/ThemeProvider'
import { AppShell } from '@/components/layout'
import { LoadingScreen } from '@/components/ui/spinner'
import { PageGate, PlatformAdminGate } from '@/components/auth/PageGate'

// Wrapper around lazy() that auto-reloads on stale chunk errors.
// After a new Vercel deploy, old chunk hashes 404. This catches the
// failed dynamic import and does a one-time hard reload to pick up
// the new index.html with fresh asset URLs.
const RELOAD_KEY = '_chunk_reload'

// Safari Private Mode throws on storage access — guard everything.
function safeSessionGet(key: string): string | null {
  try { return sessionStorage.getItem(key) } catch { return null }
}
function safeSessionSet(key: string, value: string): void {
  try { sessionStorage.setItem(key, value) } catch { /* ignore */ }
}
function safeSessionRemove(key: string): void {
  try { sessionStorage.removeItem(key) } catch { /* ignore */ }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function lazyWithReload(factory: () => Promise<{ default: ComponentType<any> }>) {
  return lazy(() =>
    factory().catch((err) => {
      // Only reload once per session to avoid infinite loops
      const hasReloaded = safeSessionGet(RELOAD_KEY)
      if (!hasReloaded) {
        safeSessionSet(RELOAD_KEY, '1')
        window.location.reload()
        // Return a never-resolving promise so React doesn't render an error
        return new Promise(() => {})
      }
      throw err
    })
  )
}

// --- Lazy-loaded pages (code-split per route) ---
const LoginPage = lazyWithReload(() => import('@/pages/auth/LoginPage').then(m => ({ default: m.LoginPage })))
const SignupPage = lazyWithReload(() => import('@/pages/auth/SignupPage').then(m => ({ default: m.SignupPage })))
const AcceptInvitePage = lazyWithReload(() => import('@/pages/auth/AcceptInvitePage').then(m => ({ default: m.AcceptInvitePage })))
const ForcePasswordChangePage = lazyWithReload(() => import('@/pages/auth/ForcePasswordChangePage').then(m => ({ default: m.ForcePasswordChangePage })))
const SetMemberPasswordPage = lazyWithReload(() => import('@/pages/auth/SetMemberPasswordPage').then(m => ({ default: m.SetMemberPasswordPage })))
const DashboardPage = lazyWithReload(() => import('@/pages/dashboard/DashboardPage').then(m => ({ default: m.DashboardPage })))
const TeamPlatePage = lazyWithReload(() => import('@/pages/dashboard/TeamPlatePage').then(m => ({ default: m.TeamPlatePage })))
const TodoThreadPage = lazyWithReload(() => import('@/pages/dashboard/TodoThreadPage').then(m => ({ default: m.TodoThreadPage })))
const InspectionsListPage = lazyWithReload(() => import('@/pages/inspections/InspectionsListPage').then(m => ({ default: m.InspectionsListPage })))
const NewInspectionPage = lazyWithReload(() => import('@/pages/inspections/NewInspectionPage').then(m => ({ default: m.NewInspectionPage })))
const ASVInspectionPage = lazyWithReload(() => import('@/pages/inspections/ASVInspectionPage').then(m => ({ default: m.ASVInspectionPage })))
const ChecklistInspectionPage = lazyWithReload(() => import('@/pages/inspections/ChecklistInspectionPage').then(m => ({ default: m.ChecklistInspectionPage })))
const FCAInspectionPage = lazyWithReload(() => import('@/pages/inspections/FCAInspectionPage').then(m => ({ default: m.FCAInspectionPage })))
const FCAEstimatePage = lazyWithReload(() => import('@/pages/inspections/FCAEstimatePage').then(m => ({ default: m.FCAEstimatePage })))
const WorkOrdersPage = lazyWithReload(() => import('@/pages/cmms/WorkOrdersPage').then(m => ({ default: m.WorkOrdersPage })))
const ArchiveWorkOrdersPage = lazyWithReload(() => import('@/pages/cmms/ArchiveWorkOrdersPage').then(m => ({ default: m.ArchiveWorkOrdersPage })))
const CreateWorkOrderPage = lazyWithReload(() => import('@/pages/cmms/CreateWorkOrderPage').then(m => ({ default: m.CreateWorkOrderPage })))
const WorkOrderDetailPage = lazyWithReload(() => import('@/pages/cmms/WorkOrderDetailPage').then(m => ({ default: m.WorkOrderDetailPage })))
const WorkOrderPipelinePage = lazyWithReload(() => import('@/pages/cmms/WorkOrderPipelinePage').then(m => ({ default: m.WorkOrderPipelinePage })))
const SchedulingPage = lazyWithReload(() => import('@/pages/scheduling/SchedulingPage').then(m => ({ default: m.SchedulingPage })))
const SubCalendarPage = lazyWithReload(() => import('@/pages/scheduling/SubCalendarPage').then(m => ({ default: m.SubCalendarPage })))
const CrewCalendarPage = lazyWithReload(() => import('@/pages/scheduling/CrewCalendarPage').then(m => ({ default: m.CrewCalendarPage })))
const EquipmentListPage = lazyWithReload(() => import('@/pages/equipment/EquipmentListPage').then(m => ({ default: m.EquipmentListPage })))
const EquipmentDetailPage = lazyWithReload(() => import('@/pages/equipment/EquipmentDetailPage').then(m => ({ default: m.EquipmentDetailPage })))
const CreateEquipmentPage = lazyWithReload(() => import('@/pages/equipment/CreateEquipmentPage').then(m => ({ default: m.CreateEquipmentPage })))
const TicketsListPage = lazyWithReload(() => import('@/pages/tickets/TicketsListPage').then(m => ({ default: m.TicketsListPage })))
const CreateTicketPage = lazyWithReload(() => import('@/pages/tickets/CreateTicketPage').then(m => ({ default: m.CreateTicketPage })))
const TicketDetailPage = lazyWithReload(() => import('@/pages/tickets/TicketDetailPage').then(m => ({ default: m.TicketDetailPage })))
const MaintenanceListPage = lazyWithReload(() => import('@/pages/maintenance/MaintenanceListPage').then(m => ({ default: m.MaintenanceListPage })))
const CreateMaintenancePage = lazyWithReload(() => import('@/pages/maintenance/CreateMaintenancePage').then(m => ({ default: m.CreateMaintenancePage })))
const MaintenanceDetailPage = lazyWithReload(() => import('@/pages/maintenance/MaintenanceDetailPage').then(m => ({ default: m.MaintenanceDetailPage })))
const SettingsPage = lazyWithReload(() => import('@/pages/settings/SettingsPage').then(m => ({ default: m.SettingsPage })))
const TeamPage = lazyWithReload(() => import('@/pages/settings/TeamPage').then(m => ({ default: m.TeamPage })))
const BrandingPage = lazyWithReload(() => import('@/pages/settings/BrandingPage').then(m => ({ default: m.BrandingPage })))
const BillingPage = lazyWithReload(() => import('@/pages/settings/BillingPage').then(m => ({ default: m.BillingPage })))
const RolesPage = lazyWithReload(() => import('@/pages/settings/RolesPage').then(m => ({ default: m.RolesPage })))
const WorkflowSettingsPage = lazyWithReload(() => import('@/pages/settings/WorkflowSettingsPage').then(m => ({ default: m.WorkflowSettingsPage })))
const NotificationSettingsPage = lazyWithReload(() => import('@/pages/settings/NotificationSettingsPage').then(m => ({ default: m.NotificationSettingsPage })))
const HelpCenterPage = lazyWithReload(() => import('@/pages/help/HelpCenterPage').then(m => ({ default: m.HelpCenterPage })))
const HelpArticlePage = lazyWithReload(() => import('@/pages/help/HelpCenterPage').then(m => ({ default: m.HelpArticlePage })))
const MessagingPage = lazyWithReload(() => import('@/pages/messaging/MessagingPage').then(m => ({ default: m.MessagingPage })))
const TourListPage = lazyWithReload(() => import('@/pages/tour/TourListPage').then(m => ({ default: m.TourListPage })))
const TourViewerPage = lazyWithReload(() => import('@/pages/tour/TourViewerPage').then(m => ({ default: m.TourViewerPage })))
const PublicTourPage = lazyWithReload(() => import('@/pages/tour/PublicTourPage').then(m => ({ default: m.PublicTourPage })))
const ProfileTourPage = lazyWithReload(() => import('@/pages/tour/ProfileTourPage').then(m => ({ default: m.ProfileTourPage })))
const LinkedTourViewerPage = lazyWithReload(() => import('@/pages/tour/LinkedTourViewerPage').then(m => ({ default: m.LinkedTourViewerPage })))
const VendorListPage = lazyWithReload(() => import('@/pages/vendors/VendorListPage').then(m => ({ default: m.VendorListPage })))
const VendorDetailPage = lazyWithReload(() => import('@/pages/vendors/VendorDetailPage').then(m => ({ default: m.VendorDetailPage })))
const ServiceCatalogPage = lazyWithReload(() => import('@/pages/inspection/ServiceCatalogPage').then(m => ({ default: m.ServiceCatalogPage })))
const VendorDashboardPage = lazyWithReload(() => import('@/pages/portals/VendorDashboardPage').then(m => ({ default: m.VendorDashboardPage })))
const ClientDashboardPage = lazyWithReload(() => import('@/pages/portals/ClientDashboardPage').then(m => ({ default: m.ClientDashboardPage })))
const ClientLocationsPage = lazyWithReload(() => import('@/pages/portals/ClientLocationsPage').then(m => ({ default: m.ClientLocationsPage })))
const ClientInspectionsPage = lazyWithReload(() => import('@/pages/portals/ClientInspectionsPage').then(m => ({ default: m.ClientInspectionsPage })))
const ClientToursPage = lazyWithReload(() => import('@/pages/portals/ClientToursPage').then(m => ({ default: m.ClientToursPage })))
const ClientWorkOrdersPage = lazyWithReload(() => import('@/pages/portals/ClientWorkOrdersPage').then(m => ({ default: m.ClientWorkOrdersPage })))
const ClientProjectPhotosPage = lazyWithReload(() => import('@/pages/portals/ClientProjectPhotosPage').then(m => ({ default: m.ClientProjectPhotosPage })))
const ClientWorkOrderDetailPage = lazyWithReload(() => import('@/pages/portals/ClientWorkOrderDetailPage').then(m => ({ default: m.ClientWorkOrderDetailPage })))
const ClientCreateWorkOrderPage = lazyWithReload(() => import('@/pages/portals/ClientCreateWorkOrderPage').then(m => ({ default: m.ClientCreateWorkOrderPage })))
const ReportsListPage = lazyWithReload(() => import('@/pages/reports/ReportsListPage').then(m => ({ default: m.ReportsListPage })))
const ReportLandingPage = lazyWithReload(() => import('@/pages/reports/ReportLandingPage').then(m => ({ default: m.ReportLandingPage })))
const PublicReportPage = lazyWithReload(() => import('@/pages/reports/PublicReportPage').then(m => ({ default: m.PublicReportPage })))
const CustomerReportPage = lazyWithReload(() => import('@/pages/reports/CustomerReportPage').then(m => ({ default: m.CustomerReportPage })))
const ClientsListPage = lazyWithReload(() => import('@/pages/clients/ClientsListPage').then(m => ({ default: m.ClientsListPage })))
const ClientDetailPage = lazyWithReload(() => import('@/pages/clients/ClientDetailPage').then(m => ({ default: m.ClientDetailPage })))
const BudgetDashboardPage = lazyWithReload(() => import('@/pages/budget/BudgetDashboardPage').then(m => ({ default: m.BudgetDashboardPage })))
const CapitalPlanPage = lazyWithReload(() => import('@/pages/capital-plan/CapitalPlanPage').then(m => ({ default: m.CapitalPlanPage })))
const FacilityHealthPage = lazyWithReload(() => import('@/pages/facility-health/FacilityHealthPage').then(m => ({ default: m.FacilityHealthPage })))
const KnowledgeBasePage = lazyWithReload(() => import('@/pages/knowledge/KnowledgeBasePage').then(m => ({ default: m.KnowledgeBasePage })))
const GoalsPage = lazyWithReload(() => import('@/pages/goals/GoalsPage').then(m => ({ default: m.GoalsPage })))
const GoalScorecardPage = lazyWithReload(() => import('@/pages/goals/GoalScorecardPage').then(m => ({ default: m.GoalScorecardPage })))
const AskOpsyriPage = lazyWithReload(() => import('@/pages/assistant/AskOpsyriPage').then(m => ({ default: m.AskOpsyriPage })))
const GoalPeriodListPage = lazyWithReload(() => import('@/pages/goals/GoalPeriodListPage').then(m => ({ default: m.GoalPeriodListPage })))
const GoalPeriodDetailPage = lazyWithReload(() => import('@/pages/goals/GoalPeriodDetailPage').then(m => ({ default: m.GoalPeriodDetailPage })))
const EditConflictsPage = lazyWithReload(() => import('@/pages/admin/EditConflictsPage').then(m => ({ default: m.EditConflictsPage })))
const VendorTradeGroupBidPage = lazyWithReload(() => import('@/pages/portals/VendorTradeGroupBidPage').then(m => ({ default: m.VendorTradeGroupBidPage })))
const VendorBidPackagePage = lazyWithReload(() => import('@/pages/portals/VendorBidPackagePage').then(m => ({ default: m.VendorBidPackagePage })))
const VendorAllBidsPage = lazyWithReload(() => import('@/pages/portals/VendorAllBidsPage').then(m => ({ default: m.VendorAllBidsPage })))
const VendorProjectsPage = lazyWithReload(() => import('@/pages/portals/VendorProjectsPage').then(m => ({ default: m.VendorProjectsPage })))
const VendorWorkOrderDetailPage = lazyWithReload(() => import('@/pages/portals/VendorWorkOrderDetailPage').then(m => ({ default: m.VendorWorkOrderDetailPage })))
const VendorPortalLayout = lazyWithReload(() => import('@/components/portals/VendorPortalLayout').then(m => ({ default: m.VendorPortalLayout })))
const ClientPortalLayout = lazyWithReload(() => import('@/components/portals/ClientPortalLayout').then(m => ({ default: m.ClientPortalLayout })))
const CrewPortalLayout = lazyWithReload(() => import('@/components/portals/CrewPortalLayout').then(m => ({ default: m.CrewPortalLayout })))
const CrewMyWeekPage = lazyWithReload(() => import('@/pages/portals/CrewMyWeekPage').then(m => ({ default: m.CrewMyWeekPage })))
const CrewTaskDetailPage = lazyWithReload(() => import('@/pages/portals/CrewTaskDetailPage').then(m => ({ default: m.CrewTaskDetailPage })))
const CrewWorkOrderPage = lazyWithReload(() => import('@/pages/portals/CrewWorkOrderPage').then(m => ({ default: m.CrewWorkOrderPage })))
const CrewDashboardPage = lazyWithReload(() => import('@/pages/cmms/CrewDashboardPage').then(m => ({ default: m.CrewDashboardPage })))
const TimesheetsPage = lazyWithReload(() => import('@/pages/cmms/TimesheetsPage').then(m => ({ default: m.TimesheetsPage })))
const VendorInboxPage = lazyWithReload(() => import('@/pages/portals/VendorInboxPage').then(m => ({ default: m.VendorInboxPage })))
const VendorTeamPage = lazyWithReload(() => import('@/pages/portals/VendorTeamPage').then(m => ({ default: m.VendorTeamPage })))
const EstimateListPage = lazyWithReload(() => import('@/pages/estimates/EstimateListPage').then(m => ({ default: m.EstimateListPage })))
const EstimateCreatePage = lazyWithReload(() => import('@/pages/estimates/EstimateCreatePage').then(m => ({ default: m.EstimateCreatePage })))
const EstimateDetailPage = lazyWithReload(() => import('@/pages/estimates/EstimateDetailPage').then(m => ({ default: m.EstimateDetailPage })))
const EstimateTemplatesPage = lazyWithReload(() => import('@/pages/estimates/EstimateTemplatesPage').then(m => ({ default: m.EstimateTemplatesPage })))
const BidWorksheetPage = lazyWithReload(() => import('@/pages/estimates/BidWorksheetPage').then(m => ({ default: m.BidWorksheetPage })))
const BidRatesPage = lazyWithReload(() => import('@/pages/estimates/BidRatesPage').then(m => ({ default: m.BidRatesPage })))
const ServiceLibraryPage = lazyWithReload(() => import('@/pages/estimates/ServiceLibraryPage').then(m => ({ default: m.ServiceLibraryPage })))
const EstimatePrintPage = lazyWithReload(() => import('@/pages/estimates/EstimatePrintPage').then(m => ({ default: m.EstimatePrintPage })))
const InvoiceListPage = lazyWithReload(() => import('@/pages/invoices/InvoiceListPage').then(m => ({ default: m.InvoiceListPage })))
const InvoiceCreatePage = lazyWithReload(() => import('@/pages/invoices/InvoiceCreatePage').then(m => ({ default: m.InvoiceCreatePage })))
const InvoiceDetailPage = lazyWithReload(() => import('@/pages/invoices/InvoiceDetailPage').then(m => ({ default: m.InvoiceDetailPage })))
const FinancePage = lazyWithReload(() => import('@/pages/finance/FinancePage').then(m => ({ default: m.FinancePage })))
const ReceiptsPage = lazyWithReload(() => import('@/pages/finance/ReceiptsPage').then(m => ({ default: m.ReceiptsPage })))
const InvoicePrintPage = lazyWithReload(() => import('@/pages/invoices/InvoicePrintPage').then(m => ({ default: m.InvoicePrintPage })))
const TakeoffListPage = lazyWithReload(() => import('@/pages/takeoffs/TakeoffListPage').then(m => ({ default: m.TakeoffListPage })))
const TakeoffEditorPage = lazyWithReload(() => import('@/pages/takeoffs/TakeoffEditorPage').then(m => ({ default: m.TakeoffEditorPage })))
const RecordingsPage = lazyWithReload(() => import('@/pages/recordings/RecordingsPage').then(m => ({ default: m.RecordingsPage })))
const FormsListPage = lazyWithReload(() => import('@/pages/forms/FormsListPage').then(m => ({ default: m.FormsListPage })))
const FormSubmissionPage = lazyWithReload(() => import('@/pages/forms/FormSubmissionPage').then(m => ({ default: m.FormSubmissionPage })))
const InventoryPage = lazyWithReload(() => import('@/pages/inventory/InventoryPage').then(m => ({ default: m.InventoryPage })))
const PartInvoiceUploadPage = lazyWithReload(() => import('@/pages/inventory/InvoiceUploadPage').then(m => ({ default: m.InvoiceUploadPage })))
const PartInvoiceDetailPage = lazyWithReload(() => import('@/pages/inventory/InvoiceDetailPage').then(m => ({ default: m.InvoiceDetailPage })))
const MyReportsPage = lazyWithReload(() => import('@/pages/my-reports/MyReportsPage').then(m => ({ default: m.MyReportsPage })))
const TeamReportsPage = lazyWithReload(() => import('@/pages/my-reports/TeamReportsPage').then(m => ({ default: m.TeamReportsPage })))
const ProspectSitesListPage = lazyWithReload(() => import('@/pages/prospects/ProspectSitesListPage').then(m => ({ default: m.ProspectSitesListPage })))
const ProspectSiteCapturePage = lazyWithReload(() => import('@/pages/prospects/ProspectSiteCapturePage').then(m => ({ default: m.ProspectSiteCapturePage })))
const ProspectPipelinePage = lazyWithReload(() => import('@/pages/prospects/ProspectPipelinePage').then(m => ({ default: m.ProspectPipelinePage })))
const ProspectSiteDetailPage = lazyWithReload(() => import('@/pages/prospects/ProspectSiteDetailPage').then(m => ({ default: m.ProspectSiteDetailPage })))
const ServiceIntakePage = lazyWithReload(() => import('@/pages/intake/ServiceIntakePage').then(m => ({ default: m.ServiceIntakePage })))
const PrivacyPolicyPage = lazyWithReload(() => import('@/pages/legal/PrivacyPolicyPage').then(m => ({ default: m.PrivacyPolicyPage })))
const SmsTermsPage = lazyWithReload(() => import('@/pages/legal/SmsTermsPage').then(m => ({ default: m.SmsTermsPage })))
const PublicPartPage = lazyWithReload(() => import('@/pages/public/PublicPartPage').then(m => ({ default: m.PublicPartPage })))
const PublicWoPhotosPage = lazyWithReload(() => import('@/pages/public/PublicWoPhotosPage').then(m => ({ default: m.PublicWoPhotosPage })))
const PublicSmsOptInPage = lazyWithReload(() => import('@/pages/public/PublicSmsOptInPage').then(m => ({ default: m.PublicSmsOptInPage })))
const LeadershipScorecardPage = lazyWithReload(() => import('@/pages/reports/LeadershipScorecardPage').then(m => ({ default: m.LeadershipScorecardPage })))
const ReceiptInboxPage = lazyWithReload(() => import('@/pages/receipts/ReceiptInboxPage').then(m => ({ default: m.ReceiptInboxPage })))
const ReorderQueuePage = lazyWithReload(() => import('@/pages/reorder/ReorderQueuePage').then(m => ({ default: m.ReorderQueuePage })))
const PurchaserQueuePage = lazyWithReload(() => import('@/pages/purchasing/PurchaserQueuePage').then(m => ({ default: m.PurchaserQueuePage })))
const PowerToolsPage = lazyWithReload(() => import('@/pages/tools/PowerToolsPage').then(m => ({ default: m.PowerToolsPage })))
const VanHandoffPage = lazyWithReload(() => import('@/pages/tools/VanHandoffPage').then(m => ({ default: m.VanHandoffPage })))
const DeliveriesPage = lazyWithReload(() => import('@/pages/deliveries/DeliveriesPage').then(m => ({ default: m.DeliveriesPage })))
const GateBoardPage = lazyWithReload(() => import('@/pages/gates/GateBoardPage').then(m => ({ default: m.GateBoardPage })))
const ProgramReportPage = lazyWithReload(() => import('@/pages/program/ProgramReportPage').then(m => ({ default: m.ProgramReportPage })))
const CloseoutReviewPage = lazyWithReload(() => import('@/pages/closeout/CloseoutReviewPage').then(m => ({ default: m.CloseoutReviewPage })))
const DispatchZonesPage = lazyWithReload(() => import('@/pages/dispatch/DispatchZonesPage').then(m => ({ default: m.DispatchZonesPage })))
const DispatchBoardPage = lazyWithReload(() => import('@/pages/dispatch/DispatchBoardPage').then(m => ({ default: m.DispatchBoardPage })))
const DispatchVendorViewPage = lazyWithReload(() => import('@/pages/dispatch/DispatchVendorViewPage').then(m => ({ default: m.DispatchVendorViewPage })))
const DispatchDashboardPage = lazyWithReload(() => import('@/pages/dispatch/DispatchDashboardPage').then(m => ({ default: m.DispatchDashboardPage })))

// DEV_BYPASS_AUTH is imported from supabase/client (env-driven)

// Detect auth-failure errors across Supabase shapes (status, code, or message)
function isAuthError(error: unknown): boolean {
  const e = error as { status?: number; code?: number | string; message?: string; name?: string }
  if (e?.status === 401 || e?.status === 403) return true
  if (e?.code === 401 || e?.code === 403) return true
  const msg = (e?.message || '').toLowerCase()
  return /jwt|token|unauthorized|not authenticated|invalid.*claim|expired/.test(msg)
}

/**
 * Collapse the burst of refreshes you'd otherwise get when a batch of queries
 * all fail on the same expired token.
 */
let refreshInFlight: Promise<unknown> | null = null
function refreshSessionOnce() {
  if (!refreshInFlight) {
    refreshInFlight = refreshSessionSafe().finally(() => { refreshInFlight = null })
  }
  return refreshInFlight
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      // Don't refetch every query when user switches windows/tabs —
      // this was piling load onto heavy WO detail + sub-task queries.
      refetchOnWindowFocus: false,
      // Don't reconnect-retry either; realtime subs handle live updates.
      refetchOnReconnect: false,
      retry: (failureCount, error) => {
        // NB: this callback must be synchronous. An async one returns a
        // Promise, which React Query reads as "truthy = retry" — every failing
        // query then retried forever.
        //
        // On 401/403 (expired token) kick off a bounded session refresh and
        // retry twice, spaced by retryDelay below, so the new token has time
        // to land without blocking this callback.
        if (isAuthError(error)) {
          if (failureCount === 0) void refreshSessionOnce()
          return failureCount < 2
        }
        const status = (error as { status?: number })?.status
          ?? (error as { code?: number })?.code
        // 5xx gets exactly ONE retry, after a long backoff (see retryDelay).
        // Never retrying at all was the safer-looking choice, but combined
        // with refetchOnWindowFocus/Reconnect: false and a 5-minute staleTime
        // it meant a single transient gateway blip left that query dead until
        // someone manually reloaded — on a page left open all day, that reads
        // as "the app stopped syncing". One spaced-out retry rides out a blip
        // without piling onto a genuinely saturated pool.
        if (typeof status === 'number' && status >= 500) return failureCount < 1
        return failureCount < 1 // default: 1 retry for other errors
      },
      // Long enough that a retry can't contribute to a thundering herd; the
      // 5xx case is the one that needs the room.
      retryDelay: (failureCount, error) => {
        // Auth retries wait on an in-flight token refresh rather than a server.
        if (isAuthError(error)) return failureCount === 0 ? 1500 : 4000
        const status = (error as { status?: number })?.status
          ?? (error as { code?: number })?.code
        if (typeof status === 'number' && status >= 500) return 4000
        return Math.min(1000 * 2 ** failureCount, 5000)
      },
    },
    mutations: {
      // Mutations (uploads, saves) silently fail after long idle when the
      // JWT expires. Force a session refresh + one retry on auth errors so
      // the user can sit on a page for 15-20 min and still save/upload.
      retry: (failureCount, error) => {
        if (isAuthError(error) && failureCount === 0) {
          void refreshSessionOnce()
          return true
        }
        return false
      },
      retryDelay: 1500,
    },
  },
})

// ─── Offline query cache ──────────────────────────────────────────
// Only the crew portal's queries are persisted, so an installed app opened
// with no service still shows the week and the task the crew is standing in
// front of. Everything else stays memory-only (the WO board is far too big
// for localStorage and is a desk tool). Cleared on every deploy via the
// build id so a shape change can never render stale data.
declare const __BUILD_ID__: string
// 'crew-active-entry' is here because a crew member underground must be able
// to see they are already on the clock; without it the punch tile renders the
// idle "Punch In" button to someone mid-shift and invites a double punch.
const OFFLINE_QUERY_PREFIXES = ['crew-portal-', 'crew-active-entry']
const OFFLINE_CACHE_MAX_AGE_MS = 1000 * 60 * 60 * 24 * 3

const queryPersister = createSyncStoragePersister({
  storage: typeof window !== 'undefined' ? window.localStorage : null,
  key: 'opsyri-query-cache',
  throttleTime: 2000,
})

const persistOptions = {
  persister: queryPersister,
  maxAge: OFFLINE_CACHE_MAX_AGE_MS,
  buster: typeof __BUILD_ID__ === 'string' ? __BUILD_ID__ : 'dev',
  dehydrateOptions: {
    shouldDehydrateQuery: (q: { queryKey: readonly unknown[]; state: { status: string } }) =>
      q.state.status === 'success'
      && typeof q.queryKey[0] === 'string'
      && OFFLINE_QUERY_PREFIXES.some((p) => (q.queryKey[0] as string).startsWith(p)),
  },
}

function OfflineSync() {
  useOutboxSync()
  usePhotoOutboxSync()
  return null
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { session, user, isLoading, isInitialized } = useAuthStore()

  // DEV MODE: Skip auth check
  if (DEV_BYPASS_AUTH) {
    return <>{children}</>
  }

  if (!isInitialized || isLoading) {
    return <LoadingScreen message="Loading..." />
  }

  if (!session) {
    return <Navigate to="/login" replace />
  }

  // Force password change for users created with temp password
  if ((user as any)?.must_change_password) {
    return <Navigate to="/change-password" replace />
  }

  return <>{children}</>
}

// Redirect root/dashboard based on portal type
function RoleAwareRedirect() {
  const user = useAuthStore((s) => s.user)

  if (user?.portal_type === 'client') {
    return <Navigate to="/portal/client" replace />
  }
  // Vendors are now regular orgs — go to normal dashboard
  return <Navigate to="/dashboard" replace />
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { session, isLoading, isInitialized } = useAuthStore()
  // IMPORTANT: use React Router's useLocation — reading window.location
  // directly creates a race during signup. When SignupPage calls
  // navigate('/work-orders/:id') right after setSession, window.location
  // updates synchronously but the route tree hasn't re-reconciled yet;
  // a re-render of PublicRoute in that window would see session=true AND
  // window.location.search='' (no vendor_invite), triggering a bogus
  // redirect to /dashboard.
  const location = useLocation()

  if (!isInitialized || isLoading) {
    return <LoadingScreen message="Loading..." />
  }

  const hasVendorInvite = new URLSearchParams(location.search).has('vendor_invite')

  if (session && !hasVendorInvite) {
    return <Navigate to="/dashboard" replace />
  }

  return <>{children}</>
}

// Placeholder pages for routes
function PlaceholderPage({ title }: { title: string }) {
  return (
    <div className="flex h-full items-center justify-center p-6">
      <div className="text-center">
        <h1 className="text-2xl font-bold">{title}</h1>
        <p className="text-muted-foreground mt-2">This page is coming soon</p>
      </div>
    </div>
  )
}

function AppRoutes() {
  return (
    <Suspense fallback={<LoadingScreen message="Loading..." />}>
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/signup"
        element={
          <PublicRoute>
            <SignupPage />
          </PublicRoute>
        }
      />

      {/* Accept invitation (public, no auth required) */}
      <Route path="/invite/:token" element={<AcceptInvitePage />} />

      {/* Set-password link for an invited pending member (public, no auth) */}
      <Route path="/set-password" element={<SetMemberPasswordPage />} />

      {/* Force password change (requires auth session but not full access) */}
      <Route path="/change-password" element={<ForcePasswordChangePage />} />

      {/* Public tour route (no auth, embeddable) */}
      <Route path="/tour/public/:tourId" element={<PublicTourPage />} />
      <Route path="/tour/p/:token" element={<ProfileTourPage />} />

      {/* Public report page (no auth, shareable) */}
      <Route path="/reports/public/:token" element={<PublicReportPage />} />

      {/* Customer-facing magazine-style report (no auth, shareable) */}
      <Route path="/report/:token" element={<CustomerReportPage />} />

      {/* Public legal pages (no auth) — required for Twilio A2P 10DLC consent */}
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/sms-terms" element={<SmsTermsPage />} />
      <Route path="/sms-opt-in" element={<PublicSmsOptInPage />} />

      {/* Public, brand-safe product page (no auth) — shareable with customers */}
      <Route path="/p/:id" element={<PublicPartPage />} />

      {/* Public work-order photo gallery (no auth) — client-visible photos only */}
      <Route path="/photos/:token" element={<PublicWoPhotosPage />} />

      {/* Invoice print view — authenticated but renders standalone (no app chrome) */}
      <Route
        path="/invoices/:id/print"
        element={
          <ProtectedRoute>
            <PageGate page="invoices"><InvoicePrintPage /></PageGate>
          </ProtectedRoute>
        }
      />

      {/* Protected routes */}
      <Route
        element={
          <ProtectedRoute>
            <AppShell />
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={<PageGate page="dashboard"><DashboardPage /></PageGate>} />
        <Route path="/team-plate" element={<PageGate page="dashboard"><TeamPlatePage /></PageGate>} />
        <Route path="/todos/:id" element={<PageGate page="dashboard"><TodoThreadPage /></PageGate>} />

        {/* Inspections */}
        <Route path="/inspections" element={<PageGate page="inspections"><InspectionsListPage /></PageGate>} />
        <Route path="/inspections/new" element={<PageGate page="inspections"><NewInspectionPage /></PageGate>} />
        <Route path="/inspections/asv/:id" element={<PageGate page="inspections"><ASVInspectionPage /></PageGate>} />
        <Route path="/inspections/checklist/:id" element={<PageGate page="inspections"><ChecklistInspectionPage /></PageGate>} />
        <Route path="/inspections/fca/:id" element={<PageGate page="inspections"><FCAInspectionPage /></PageGate>} />
        <Route path="/inspections/fca/:id/estimate" element={<PageGate page="inspections"><FCAEstimatePage /></PageGate>} />
        {/* Bare /inspections/:id has no type context (real inspections use
            /inspections/asv|fca|checklist/:id) — redirect stale links to the list. */}
        <Route path="/inspections/:id" element={<Navigate to="/inspections" replace />} />

        {/* Service Catalog & Checklists */}
        <Route path="/service-catalog" element={<PageGate page="inspections"><ServiceCatalogPage /></PageGate>} />

        {/* Estimates */}
        <Route path="/estimates" element={<PageGate page="estimates"><EstimateListPage /></PageGate>} />
        <Route path="/estimates/new" element={<PageGate page="estimates"><EstimateCreatePage /></PageGate>} />
        <Route path="/estimates/templates" element={<PageGate page="estimates"><EstimateTemplatesPage /></PageGate>} />
        <Route path="/estimates/rates" element={<PageGate page="estimates"><BidRatesPage /></PageGate>} />
        <Route path="/estimates/services" element={<PageGate page="estimates"><ServiceLibraryPage /></PageGate>} />
        <Route path="/estimates/:id" element={<PageGate page="estimates"><EstimateDetailPage /></PageGate>} />
        <Route path="/estimates/:id/edit" element={<PageGate page="estimates"><EstimateCreatePage /></PageGate>} />
        <Route path="/estimates/:id/worksheet" element={<PageGate page="estimates"><BidWorksheetPage /></PageGate>} />
        <Route path="/estimates/:id/print" element={<PageGate page="estimates"><EstimatePrintPage /></PageGate>} />

        {/* Finance */}
        <Route path="/finance" element={<Navigate to="/finance/billing" replace />} />
        <Route path="/finance/billing" element={<PageGate page="finance"><FinancePage /></PageGate>} />
        <Route path="/finance/receipts" element={<PageGate page="receipts"><ReceiptsPage /></PageGate>} />
        <Route path="/invoices" element={<PageGate page="invoices"><InvoiceListPage /></PageGate>} />
        <Route path="/invoices/new" element={<PageGate page="invoices"><InvoiceCreatePage /></PageGate>} />
        <Route path="/invoices/:id" element={<PageGate page="invoices"><InvoiceDetailPage /></PageGate>} />
        <Route path="/invoices/:id/edit" element={<PageGate page="invoices"><InvoiceCreatePage /></PageGate>} />

        {/* Blueprint Takeoffs */}
        <Route path="/takeoffs" element={<PageGate page="takeoffs"><TakeoffListPage /></PageGate>} />
        <Route path="/takeoffs/:id" element={<PageGate page="takeoffs"><TakeoffEditorPage /></PageGate>} />

        {/* Forms */}
        <Route path="/forms" element={<PageGate page="forms"><FormsListPage /></PageGate>} />
        <Route path="/forms/submit/:id" element={<PageGate page="forms"><FormSubmissionPage /></PageGate>} />

        {/* Screen Recordings */}
        <Route path="/recordings" element={<PageGate page="recordings"><RecordingsPage /></PageGate>} />

        {/* Service Tickets */}
        <Route path="/tickets" element={<PageGate page="service_tickets"><TicketsListPage /></PageGate>} />
        <Route path="/tickets/new" element={<PageGate page="service_tickets"><CreateTicketPage /></PageGate>} />
        <Route path="/tickets/:id" element={<PageGate page="service_tickets"><TicketDetailPage /></PageGate>} />

        {/* Clients */}
        <Route path="/clients" element={<PageGate page="clients"><ClientsListPage /></PageGate>} />
        <Route path="/clients/:id" element={<PageGate page="clients"><ClientDetailPage /></PageGate>} />

        {/* Equipment */}
        <Route path="/equipment" element={<PageGate page="equipment"><EquipmentListPage /></PageGate>} />
        <Route path="/equipment/new" element={<PageGate page="equipment"><CreateEquipmentPage /></PageGate>} />
        <Route path="/equipment/:id" element={<PageGate page="equipment"><EquipmentDetailPage /></PageGate>} />

        {/* Parts inventory — view open to all internal staff; edit gated by can_manage_parts */}
        <Route path="/inventory" element={<PageGate page="parts_inventory"><InventoryPage /></PageGate>} />
        <Route path="/inventory/invoices" element={<PageGate page="parts_inventory"><PartInvoiceUploadPage /></PageGate>} />
        <Route path="/inventory/invoices/:id" element={<PageGate page="parts_inventory"><PartInvoiceDetailPage /></PageGate>} />

        {/* CMMS - Work Orders */}
        <Route path="/work-orders" element={<PageGate page="work_orders"><WorkOrdersPage /></PageGate>} />
        <Route path="/work-orders/new" element={<PageGate page="work_orders"><CreateWorkOrderPage /></PageGate>} />
        {/* Archive boards (2021-2025): consolidated read-only page with an
            optional ?year= URL param for drill-down. Slug path kept as a
            redirect-friendly alias so old /work-orders/archive/2023 links
            still land in the right place. */}
        <Route path="/work-orders/archive" element={<PageGate page="wo_archive"><ArchiveWorkOrdersPage /></PageGate>} />
        <Route path="/work-orders/archive/:slug" element={<PageGate page="wo_archive"><ArchiveWorkOrdersPage /></PageGate>} />
        <Route path="/work-orders/pipeline" element={<PageGate page="work_orders"><WorkOrderPipelinePage /></PageGate>} />
        <Route path="/work-orders/:id" element={<PageGate page="work_orders"><WorkOrderDetailPage /></PageGate>} />

        {/* CMMS - Scheduling */}
        <Route path="/scheduling" element={<PageGate page="scheduling"><SchedulingPage /></PageGate>} />
        <Route path="/sub-calendar" element={<PageGate page="sub_calendar"><SubCalendarPage /></PageGate>} />
        <Route path="/crew-calendar" element={<PageGate page="crew_calendar"><CrewCalendarPage /></PageGate>} />

        {/* CMMS - Crew Dashboard (admin view of each member's week) */}
        <Route path="/crew-dashboard" element={<PageGate page="crew_dashboard"><CrewDashboardPage /></PageGate>} />

        {/* CMMS - Payroll Timesheets */}
        <Route path="/timesheets" element={<PageGate page="timesheets"><TimesheetsPage /></PageGate>} />

        {/* CMMS - Maintenance Schedules */}
        <Route path="/maintenance" element={<PageGate page="pm_schedules"><MaintenanceListPage /></PageGate>} />
        <Route path="/maintenance/new" element={<PageGate page="pm_schedules"><CreateMaintenancePage /></PageGate>} />
        <Route path="/maintenance/:id" element={<PageGate page="pm_schedules"><MaintenanceDetailPage /></PageGate>} />

        {/* CMMS - Vendors */}
        <Route path="/vendors" element={<PageGate page="vendors"><VendorListPage /></PageGate>} />
        <Route path="/vendors/new" element={<PageGate page="vendors"><VendorListPage /></PageGate>} />
        <Route path="/vendors/:id" element={<PageGate page="vendors"><VendorDetailPage /></PageGate>} />

        {/* Bid System v2 admin tools */}
        <Route path="/admin/edit-conflicts" element={<PlatformAdminGate><EditConflictsPage /></PlatformAdminGate>} />

        {/* 360° Virtual Tours */}
        <Route path="/tours" element={<PageGate page="360_tours"><TourListPage /></PageGate>} />
        <Route path="/tours/:id" element={<PageGate page="360_tours"><TourViewerPage /></PageGate>} />
        <Route path="/tours/:id/edit" element={<PageGate page="360_tours"><TourViewerPage /></PageGate>} />
        <Route path="/tours/group/:groupId" element={<PageGate page="360_tours"><LinkedTourViewerPage /></PageGate>} />

        {/* Knowledge Base */}
        <Route path="/knowledge" element={<PageGate page="knowledge_base"><KnowledgeBasePage /></PageGate>} />

        {/* FM Budget */}
        <Route path="/budget" element={<PageGate page="fm_budget"><BudgetDashboardPage /></PageGate>} />

        {/* Capital Plan */}
        <Route path="/capital-plan" element={<PageGate page="capital_plan"><CapitalPlanPage /></PageGate>} />

        {/* Facility Health */}
        <Route path="/facility-health" element={<PageGate page="facility_health"><FacilityHealthPage /></PageGate>} />

        {/* Employee Goals */}
        <Route path="/goals" element={<PageGate page="goals"><GoalsPage /></PageGate>} />
        <Route path="/goals/scorecard" element={<PageGate page="goals"><GoalScorecardPage /></PageGate>} />
        <Route path="/goals/:periodType" element={<PageGate page="goals"><GoalPeriodListPage /></PageGate>} />
        <Route path="/goals/:periodType/:periodId" element={<PageGate page="goals"><GoalPeriodDetailPage /></PageGate>} />

        {/* Reports */}
        <Route path="/reports" element={<PageGate page="reports"><ReportsListPage /></PageGate>} />
        <Route path="/reports/:id" element={<PageGate page="reports"><ReportLandingPage /></PageGate>} />

        {/* Prospect Sites (Cornerstone-only field-capture lead system) */}
        <Route path="/prospects" element={<PageGate page="prospect_sites"><ProspectSitesListPage /></PageGate>} />
        <Route path="/prospects/pipeline" element={<PageGate page="prospect_sites"><ProspectPipelinePage /></PageGate>} />
        <Route path="/prospects/capture" element={<PageGate page="prospect_sites"><ProspectSiteCapturePage /></PageGate>} />
        <Route path="/prospects/:id" element={<PageGate page="prospect_sites"><ProspectSiteDetailPage /></PageGate>} />

        {/* My activity reports (daily + weekly user rollups) */}
        <Route path="/my-reports" element={<MyReportsPage />} />
        <Route path="/team-reports" element={<PageGate page="team_reports"><TeamReportsPage /></PageGate>} />

        {/* Settings */}
        <Route path="/scorecard" element={<PageGate page="scorecard"><LeadershipScorecardPage /></PageGate>} />
        <Route path="/assistant" element={<PageGate page="ask_opsyri"><AskOpsyriPage /></PageGate>} />
        <Route path="/receipts-inbox" element={<PageGate page="receipts_inbox"><ReceiptInboxPage /></PageGate>} />
        <Route path="/reorder-queue" element={<PageGate page="reorder_queue"><ReorderQueuePage /></PageGate>} />
        <Route path="/purchasing" element={<PageGate page="reorder_queue"><PurchaserQueuePage /></PageGate>} />
        <Route path="/deliveries" element={<PageGate page="deliveries"><DeliveriesPage /></PageGate>} />
        <Route path="/tools" element={<PageGate page="power_tools"><PowerToolsPage /></PageGate>} />
        <Route path="/tools/handoffs/new" element={<PageGate page="power_tools"><VanHandoffPage /></PageGate>} />
        <Route path="/tools/handoffs/:id" element={<PageGate page="power_tools"><VanHandoffPage /></PageGate>} />
        <Route path="/gates" element={<PageGate page="gate_board"><GateBoardPage /></PageGate>} />
        <Route path="/program" element={<PageGate page="program_report"><ProgramReportPage /></PageGate>} />
        <Route path="/closeout-review" element={<PageGate page="closeout_review"><CloseoutReviewPage /></PageGate>} />
        <Route path="/dispatch" element={<PageGate page="dispatch"><DispatchDashboardPage /></PageGate>} />
        <Route path="/dispatch-zones" element={<PageGate page="dispatch"><DispatchZonesPage /></PageGate>} />
        <Route path="/dispatch-board" element={<PageGate page="dispatch"><DispatchBoardPage /></PageGate>} />
        <Route path="/dispatch-vendor-view" element={<PageGate page="dispatch"><DispatchVendorViewPage /></PageGate>} />
        <Route path="/settings" element={<PageGate page="settings"><SettingsPage /></PageGate>} />
        <Route path="/settings/profile" element={<PageGate page="settings"><SettingsPage /></PageGate>} />
        <Route path="/settings/team" element={<PageGate page="team_management"><TeamPage /></PageGate>} />
        <Route path="/settings/branding" element={<PageGate page="settings"><BrandingPage /></PageGate>} />
        <Route path="/settings/billing" element={<PageGate page="billing"><BillingPage /></PageGate>} />
        <Route path="/settings/roles" element={<PageGate page="roles"><RolesPage /></PageGate>} />
        <Route path="/settings/workflow" element={<PageGate page="workflow"><WorkflowSettingsPage /></PageGate>} />
        <Route path="/settings/notifications" element={<PageGate page="settings"><NotificationSettingsPage /></PageGate>} />

        {/* Help / How-To — open to every signed-in user */}
        <Route path="/help" element={<HelpCenterPage />} />
        <Route path="/help/:slug" element={<HelpArticlePage />} />

        {/* Messaging */}
        <Route path="/messages" element={<PageGate page="messaging"><MessagingPage /></PageGate>} />

        {/* Service Intake — shared SMS inbox (Twilio); calls in Phase 2 */}
        <Route path="/intake" element={<PageGate page="service_intake"><ServiceIntakePage /></PageGate>} />

        {/* More (mobile) */}

        {/* Portal Routes */}
        <Route path="/portal/vendor" element={<VendorPortalLayout />}>
          <Route index element={<VendorDashboardPage />} />
          <Route path="rfqs" element={<VendorAllBidsPage />} />
          <Route path="projects" element={<VendorProjectsPage />} />
          <Route path="work-orders" element={<Navigate to="/portal/vendor/projects" replace />} />
          {/* Deep links from bid invites, the inbox, and the cross-org redirect
              in the admin WO page all land here — resolve to the vendor's bid. */}
          <Route path="work-orders/:id" element={<VendorWorkOrderDetailPage />} />
          <Route path="trade-bids/:bidId" element={<VendorTradeGroupBidPage />} />
          <Route path="bid-packages/:packageId" element={<VendorBidPackagePage />} />
          <Route path="inbox" element={<VendorInboxPage />} />
          <Route path="team" element={<VendorTeamPage />} />
        </Route>
        <Route path="/portal/crew" element={<PageGate page="crew_week"><CrewPortalLayout /></PageGate>}>
          <Route index element={<CrewMyWeekPage />} />
          <Route path="task/:taskId" element={<CrewTaskDetailPage />} />
          <Route path="work-orders/:woId" element={<CrewWorkOrderPage />} />
          <Route path="member/:userId" element={<CrewMyWeekPage />} />
          <Route path="member/:userId/task/:taskId" element={<CrewTaskDetailPage />} />
          <Route path="member/:userId/work-orders/:woId" element={<CrewWorkOrderPage />} />
        </Route>
        <Route path="/portal/client" element={<ClientPortalLayout />}>
          <Route index element={<ClientDashboardPage />} />
          <Route path="locations" element={<ClientLocationsPage />} />
          <Route path="inspections" element={<PageGate page="inspections"><ClientInspectionsPage /></PageGate>} />
          <Route path="tours" element={<ClientToursPage />} />
          <Route path="work-orders" element={<ClientWorkOrdersPage />} />
          <Route path="photos" element={<ClientProjectPhotosPage />} />
          <Route path="work-orders/new" element={<ClientCreateWorkOrderPage />} />
          <Route path="work-orders/:id" element={<ClientWorkOrderDetailPage />} />
        </Route>

        {/* Admin preview route for client WO creation */}
        <Route path="/client-work-orders/new" element={<ClientCreateWorkOrderPage clientIdOverride="20000000-0000-0000-0000-000000000001" />} />
      </Route>

      {/* Redirect root based on role */}
      <Route path="/" element={<RoleAwareRedirect />} />

      {/* 404 */}
      <Route path="*" element={<PlaceholderPage title="Page Not Found" />} />
    </Routes>
    </Suspense>
  )
}

export default function App() {
  // Clear the chunk-reload flag on successful app mount so future
  // deploys can trigger a reload again
  safeSessionRemove(RELOAD_KEY)

  // Notice a new deploy and move forward onto it. Installed PWAs on iOS
  // otherwise sit on a cached start URL and never pick up a release.
  useBuildVersionCheck()

  return (
    <PersistQueryClientProvider client={queryClient} persistOptions={persistOptions}>
      <BrowserRouter>
        <AuthProvider>
          <ThemeProvider>
            <OfflineSync />
            <AppRoutes />
          </ThemeProvider>
        </AuthProvider>
      </BrowserRouter>
    </PersistQueryClientProvider>
  )
}
